import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Collapse, IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { motion } from 'framer-motion';
import api from '../../../services/api';
import type { OrderPriceRow, PriceProductRow, ProductCost, SalesPrices } from '../../../types/factory';
import { EmptyState, KpiItem, KpiStrip, PageHeader, Section, StatusPill, TONES, Tone } from '../../../components/ui/PageKit';
import { addDaysIso, errorMessage, fmtMoney, fmtMoney2, fmtQty, todayIso } from '../shared';
import { AnimatedNumber } from '../plant/ui';
import PriceHistorySection from './PriceHistorySection';

// Precios de venta reales contra el costeo: a cuánto se vendió cada producto (promedio ponderado, último precio,
// por cliente y por mes) y cuánto deja. Los precios salen de las ventas con ítems (libros de cuenta corriente y
// facturas); el costo, de la pantalla de Costeo.

type Periodo = '3' | '6' | '12' | 'todo';
const PERIODOS: { id: Periodo; label: string }[] = [
  { id: '3', label: '3 meses' }, { id: '6', label: '6 meses' }, { id: '12', label: '12 meses' }, { id: 'todo', label: 'Todo' },
];

const marginTone = (m: number | null): Tone => (m == null ? 'neutral' : m < 0 ? 'bad' : m < 0.15 ? 'warn' : 'ok');
const pct = (m: number | null) => (m == null ? '—' : `${(m * 100).toFixed(1).replace('.', ',')}%`);
const monthLabel = (m: string) => {
  const d = new Date(`${m}-15T12:00:00`);
  return d.toLocaleDateString('es-AR', { month: 'short', year: '2-digit' }).replace('.', '');
};

const FactoryPricesPage: React.FC = () => {
  const [periodo, setPeriodo] = useState<Periodo>('12');
  const [data, setData] = useState<SalesPrices | null>(null);
  const [costs, setCosts] = useState<Record<number, ProductCost>>({});
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    const to = todayIso();
    const from = periodo === 'todo' ? '2018-01-01' : addDaysIso(to, -Math.round(Number(periodo) * 30.44));
    setError(null);
    api.getSalesPrices({ from, to }).then(setData).catch(e => setError(errorMessage(e, 'No se pudieron traer los precios')));
  }, [periodo]);

  useEffect(() => {
    api.getFactoryCosts()
      .then(c => setCosts(Object.fromEntries(c.rows.map(r => [r.productId, r]))))
      .catch(() => setCosts({}));
  }, []);

  const unitCost = (id: number) => {
    const c = costs[id];
    return c ? (c.totalCost || c.currentCost || null) : null;
  };
  const margin = (price: number, cost: number | null) => (cost == null || !price ? null : (price - cost) / price);

  const totals = useMemo(() => {
    const rows = data?.products ?? [];
    const revenue = rows.reduce((a, r) => a + r.revenue, 0);
    const withCost = rows.filter(r => unitCost(r.productId) != null);
    const costOfSales = withCost.reduce((a, r) => a + (unitCost(r.productId) ?? 0) * r.quantity, 0);
    const revenueWithCost = withCost.reduce((a, r) => a + r.revenue, 0);
    const below = rows.filter(r => { const m = margin(r.avgPrice, unitCost(r.productId)); return m != null && m < 0; }).length;
    return { revenue, below, margin: revenueWithCost ? (revenueWithCost - costOfSales) / revenueWithCost : null, products: rows.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, costs]);

  return (
    <>
      <PageHeader
        title="Precios y márgenes"
        description="A cuánto se vendió cada producto de verdad (promedio ponderado y último precio, por cliente y por mes) contra su costo del Costeo. Los precios salen de las ventas y facturas con detalle de producto."
        actions={(
          <ToggleButtonGroup size="small" exclusive value={periodo} onChange={(_, v) => v && setPeriodo(v)}>
            {PERIODOS.map(p => <ToggleButton key={p.id} value={p.id} sx={{ textTransform: 'none', px: 1.5 }}>{p.label}</ToggleButton>)}
          </ToggleButtonGroup>
        )}
      />
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <KpiStrip>
        <KpiItem label="Facturado en el período" value={<AnimatedNumber value={totals.revenue} prefix="$" />} hint={`${totals.products} productos`} />
        <KpiItem label="Margen sobre el costeo" value={pct(totals.margin)} tone={marginTone(totals.margin)} hint="ponderado por lo vendido" />
        <KpiItem label="Productos bajo costo" value={<AnimatedNumber value={totals.below} />} tone={totals.below ? 'bad' : 'ok'} hint={totals.below ? 'se venden por debajo de lo que cuestan' : 'todos cubren su costo'} />
        <KpiItem label="OC de gaseras" value={<AnimatedNumber value={data?.orderPrices.length ?? 0} />} hint="con su precio por tapón" />
      </KpiStrip>

      <Section title="Por producto" description="Tocá un producto para ver a quién se le vendió y cómo fue cambiando el precio mes a mes.">
        {!data ? <Box sx={{ height: 120 }} /> : data.products.length === 0 ? (
          <EmptyState title="Todavía no hay ventas con detalle de producto en el período" hint="Se llenan con el historial de las cuentas corrientes y las facturas, o con las ventas que se carguen desde ahora." />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 36 }} />
                <TableCell>Producto</TableCell>
                <TableCell align="right">Vendido</TableCell>
                <TableCell align="right">Facturado</TableCell>
                <TableCell align="right">Precio promedio</TableCell>
                <TableCell align="right">Último precio</TableCell>
                <TableCell align="right">Precio de lista</TableCell>
                <TableCell align="right">Costo</TableCell>
                <TableCell align="right">Margen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.products.map(r => (
                <ProductRow key={r.productId} row={r} cost={unitCost(r.productId)} open={open === r.productId}
                  onToggle={() => setOpen(open === r.productId ? null : r.productId)} margin={margin(r.avgPrice, unitCost(r.productId))} />
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <PriceHistorySection />

      <OrderPrices rows={data?.orderPrices ?? []} costs={costs} />
    </>
  );
};

const ProductRow: React.FC<{ row: PriceProductRow; cost: number | null; margin: number | null; open: boolean; onToggle: () => void }> = ({
  row, cost, margin, open, onToggle,
}) => (
  <>
    <TableRow hover onClick={onToggle} sx={{ cursor: 'pointer', '& > td': { borderBottom: open ? 0 : undefined } }}>
      <TableCell><IconButton size="small" aria-label="Ver detalle">{open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</IconButton></TableCell>
      <TableCell sx={{ fontWeight: 650 }}>{row.productName}</TableCell>
      <TableCell align="right">{fmtQty(row.quantity)} {row.unit || 'u.'}</TableCell>
      <TableCell align="right">{fmtMoney(row.revenue)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney2(row.avgPrice)}</TableCell>
      <TableCell align="right">{fmtMoney2(row.lastPrice)}</TableCell>
      <TableCell align="right" sx={{ color: 'text.secondary' }}>{row.listPrice ? fmtMoney2(row.listPrice) : '—'}</TableCell>
      <TableCell align="right">{cost != null ? fmtMoney2(cost) : <Typography component="span" variant="body2" color="text.secondary">sin costeo</Typography>}</TableCell>
      <TableCell align="right"><StatusPill tone={marginTone(margin)}>{pct(margin)}</StatusPill></TableCell>
    </TableRow>
    <TableRow>
      <TableCell colSpan={9} sx={{ p: 0 }}>
        <Collapse in={open} unmountOnExit>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1.1fr) minmax(0,1fr)' }, gap: 3, p: 2.5, bgcolor: '#FBFAF8' }}>
            <Box>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Por cliente</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Unidades</TableCell>
                    <TableCell align="right">Promedio</TableCell>
                    <TableCell align="right">Último</TableCell>
                    <TableCell align="right">Margen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.customers.map(c => {
                    const m = cost == null || !c.avgPrice ? null : (c.avgPrice - cost) / c.avgPrice;
                    return (
                      <TableRow key={`${c.customerId}-${c.customerName}`}>
                        <TableCell>{c.customerName}<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{c.orders} venta{c.orders === 1 ? '' : 's'} · última {new Date(c.lastDate).toLocaleDateString('es-AR')}</Typography></TableCell>
                        <TableCell align="right">{fmtQty(c.quantity)}</TableCell>
                        <TableCell align="right">{fmtMoney2(c.avgPrice)}</TableCell>
                        <TableCell align="right">{fmtMoney2(c.lastPrice)}</TableCell>
                        <TableCell align="right"><StatusPill tone={marginTone(m)}>{pct(m)}</StatusPill></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <MonthlyChart months={row.months} cost={cost} />
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  </>
);

/** Precio promedio por mes (barras) contra el costo (línea punteada). */
const MonthlyChart: React.FC<{ months: PriceProductRow['months']; cost: number | null }> = ({ months, cost }) => {
  const max = Math.max(1, cost ?? 0, ...months.map(m => m.avgPrice)) * 1.08;
  const H = 170;
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 700, flex: 1 }}>Precio promedio por mes</Typography>
        {cost != null && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 18, borderTop: `2px dashed ${TONES.bad.dot}` }} />
            <Typography variant="caption" color="text.secondary">costo {fmtMoney2(cost)}</Typography>
          </Stack>
        )}
      </Stack>
      <Box sx={{ position: 'relative', height: H + 22, overflowX: 'auto' }}>
        <Stack direction="row" spacing={0.75} alignItems="flex-end" sx={{ height: H, minWidth: months.length * 34 }}>
          {months.map((m, i) => {
            const below = cost != null && m.avgPrice < cost;
            return (
              <Stack key={m.month} alignItems="center" sx={{ flex: 1, minWidth: 28, height: '100%', justifyContent: 'flex-end' }}>
                <motion.div
                  title={`${monthLabel(m.month)}: ${fmtMoney2(m.avgPrice)} · ${fmtQty(m.quantity)} u.`}
                  style={{ width: '100%', borderRadius: 5, background: below ? TONES.bad.dot : '#F0561D' }}
                  initial={{ height: 0 }}
                  animate={{ height: `${(m.avgPrice / max) * 100}%` }}
                  transition={{ duration: 0.7, delay: i * 0.03, ease: [0.16, 1, 0.3, 1] }}
                />
              </Stack>
            );
          })}
        </Stack>
        {cost != null && (
          <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 22 + (cost / max) * H, borderTop: `2px dashed ${TONES.bad.dot}`, pointerEvents: 'none' }} />
        )}
        <Stack direction="row" spacing={0.75} sx={{ minWidth: months.length * 34, mt: 0.5 }}>
          {months.map(m => (
            <Typography key={m.month} sx={{ flex: 1, minWidth: 28, fontSize: 10.5, color: 'text.secondary', textAlign: 'center' }}>{monthLabel(m.month)}</Typography>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

const OrderPrices: React.FC<{ rows: OrderPriceRow[]; costs: Record<number, ProductCost> }> = ({ rows, costs }) => {
  const costByName = useMemo(() => Object.fromEntries(Object.values(costs).map(c => [c.productName, c.totalCost || c.currentCost])), [costs]);
  if (!rows.length) return null;
  return (
    <Section title="Precios de las OC de gaseras" description="Precio pactado por unidad en cada orden de compra, cuánto se entregó y qué margen deja contra el costeo.">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Cliente</TableCell>
            <TableCell>OC</TableCell>
            <TableCell>Fecha</TableCell>
            <TableCell align="right">Precio</TableCell>
            <TableCell align="right">Cantidad</TableCell>
            <TableCell align="right">Entregado</TableCell>
            <TableCell align="right">Margen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(o => {
            const cost = costByName[o.productName];
            const m = !o.unitPrice || !cost ? null : (o.unitPrice - cost) / o.unitPrice;
            const done = o.quantity ? Math.min(1, o.delivered / o.quantity) : 0;
            return (
              <TableRow key={o.orderId} hover>
                <TableCell sx={{ fontWeight: 600 }}>{o.customerName}</TableCell>
                <TableCell>{o.number}</TableCell>
                <TableCell>{new Date(o.date).toLocaleDateString('es-AR')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{o.unitPrice ? fmtMoney2(o.unitPrice) : <Typography component="span" variant="body2" color="text.secondary">sin precio</Typography>}</TableCell>
                <TableCell align="right">{fmtQty(o.quantity)}</TableCell>
                <TableCell align="right">
                  <Stack alignItems="flex-end" spacing={0.25}>
                    <span>{fmtQty(o.delivered)}</span>
                    <Paper elevation={0} sx={{ width: 64, height: 4, bgcolor: '#ECEAE6', borderRadius: 2, overflow: 'hidden' }}>
                      <Box sx={{ width: `${done * 100}%`, height: '100%', bgcolor: done >= 1 ? TONES.ok.dot : '#F0561D' }} />
                    </Paper>
                  </Stack>
                </TableCell>
                <TableCell align="right"><StatusPill tone={marginTone(m)}>{pct(m)}</StatusPill></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Section>
  );
};

export default FactoryPricesPage;
